import { useState } from "react";
import { Upload, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, downloadFile, ROLE_LABELS } from "@/lib/api";
import { Table, Pill } from "@/components/common";

const cols = [{ k: "row", h: "Row" }, { k: "name", h: "Name" }, { k: "email", h: "Email" },
  { k: "role", h: "Role", r: (x) => ROLE_LABELS[x.role] || x.role_input }, { k: "title", h: "Title" },
  { k: "ok", h: "Check", r: (x) => (x.ok ? <Pill tone="green">ready</Pill> : <Pill tone="red">{x.error}</Pill>) }];

export function BulkInvite({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (f, commit) => {
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      return (await api.post(`/users/import?commit=${commit}`, fd)).data;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    } finally {
      setBusy(false);
    }
  };
  const pick = async (f) => {
    setFile(f);
    setPreview(f ? await send(f, false) : null);
  };
  const close = (o) => { if (!o) { setFile(null); setPreview(null); } onOpenChange(o); };
  const commit = async () => {
    const r = await send(file, true);
    if (!r) return;
    toast.success(`${r.created} invited — welcome emails are on their way${r.skipped ? ` (${r.skipped} skipped)` : ""}`);
    onDone();
    close(false);
  };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="bulk-invite-dialog" className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display">Bulk invite users</DialogTitle>
          <DialogDescription>Upload an Excel (.xlsx) or CSV file with columns name, email, role and title (optional). Each person gets a welcome email to set their own password.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" data-testid="template-xlsx-btn" onClick={() => downloadFile("/users/import-template?format=xlsx", "aspire-users-template.xlsx")}><Download className="mr-1.5 h-4 w-4" />Excel template</Button>
          <Button variant="outline" size="sm" data-testid="template-csv-btn" onClick={() => downloadFile("/users/import-template?format=csv", "aspire-users-template.csv")}><Download className="mr-1.5 h-4 w-4" />CSV template</Button>
        </div>
        <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-6 text-sm text-slate-600 transition-colors hover:border-indigo-400 hover:bg-indigo-50/40">
          {busy ? <Loader2 className="h-6 w-6 animate-spin text-indigo-600" /> : <Upload className="h-6 w-6 text-indigo-600" />}
          <span data-testid="bulk-file-name">{file ? file.name : "Choose a .xlsx or .csv file"}</span>
          <input type="file" accept=".xlsx,.csv" className="hidden" data-testid="bulk-file-input" onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
        </label>
        {preview && (
          <>
            <div className="text-sm" data-testid="bulk-summary"><b>{preview.valid}</b> ready to invite · <b>{preview.invalid}</b> with problems (will be skipped)</div>
            <Table rows={preview.rows} cols={cols} />
          </>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="bulk-invite-cancel" onClick={() => close(false)}>Cancel</Button>
          <Button data-testid="bulk-invite-submit" disabled={!preview?.valid || busy} onClick={commit}>Invite {preview?.valid || 0} {preview?.valid === 1 ? "person" : "people"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
